import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const { language } = useLanguage();
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Setup admin on first load
    const setupAdmin = async () => {
      try {
        await axios.post(`${API}/auth/setup`);
      } catch (error) {
        // Admin already exists, ignore
      }
    };
    setupAdmin();
  }, []);

  useEffect(() => {
    if (user) {
      navigate('/admin');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success(language === 'sv' ? 'Inloggad!' : 'Logged in!');
      navigate('/admin');
    } catch (error) {
      toast.error(language === 'sv' ? 'Felaktiga uppgifter' : 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white border border-stone-200 p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-stone-900 mb-2">FLOWINGO</h1>
            <p className="text-stone-500">
              {language === 'sv' ? 'Logga in till admin-panelen' : 'Log in to admin panel'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="username" className="text-stone-700">
                {language === 'sv' ? 'Användarnamn' : 'Username'}
              </Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="pl-10 bg-white border-stone-300 text-stone-900 rounded-none"
                  placeholder={language === 'sv' ? 'Ange användarnamn' : 'Enter username'}
                  required
                  data-testid="login-username"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-stone-700">
                {language === 'sv' ? 'Lösenord' : 'Password'}
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-white border-stone-300 text-stone-900 rounded-none"
                  placeholder={language === 'sv' ? 'Ange lösenord' : 'Enter password'}
                  required
                  data-testid="login-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-stone-900 text-white hover:bg-stone-800 rounded-none h-11"
              data-testid="login-submit"
            >
              {loading ? '...' : (language === 'sv' ? 'Logga in' : 'Log in')}
            </Button>
          </form>

        </div>
      </div>
    </div>
  );
};

export default LoginPage;
